import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import './WebhookEventsFeed.css'

interface WebhookEvent {
  id: string
  type: string
  container_name: string
  run_id: string | null
  timestamp: string
  data: string
  created_at: string
}

interface WebhookEventsFeedProps {
  containerName?: string
}

function WebhookEventsFeed({ containerName }: WebhookEventsFeedProps) {
  const [events, setEvents] = useState<WebhookEvent[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [collapsed, setCollapsed] = useState(true)

  const fetchEvents = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ limit: '100' })
      if (containerName) {
        params.set('container_name', containerName)
      }
      const res = await fetch(`/api/webhook-events?${params.toString()}`)
      if (!res.ok) throw new Error('Failed to fetch webhook events')
      const data = await res.json() as WebhookEvent[]
      setEvents(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch webhook events')
    } finally {
      setLoading(false)
    }
  }, [containerName])

  useEffect(() => {
    if (!collapsed) {
      fetchEvents()
    }
  }, [collapsed, fetchEvents])

  function formatTimestamp(ts: string): string {
    const date = new Date(ts)
    return date.toLocaleString('en-US', {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    })
  }

  function getEventTypeClass(type: string): string {
    if (type === 'container.error') return 'event-type-error'
    if (type.startsWith('container.')) return 'event-type-container'
    if (type.startsWith('message.')) return 'event-type-message'
    if (type.startsWith('task.')) return 'event-type-task'
    if (type === 'log') return 'event-type-log'
    return ''
  }

  function parseData(data: string): Record<string, unknown> {
    try {
      return typeof data === 'string' ? JSON.parse(data) : data as Record<string, unknown>
    } catch {
      return {}
    }
  }

  function formatEventData(type: string, data: Record<string, unknown>): string {
    switch (type) {
      case 'container.started':
        return [
          data.revision && `rev: ${String(data.revision).slice(0, 8)}`,
        ].filter(Boolean).join(' · ') || 'Started'
      case 'container.idle':
        return `${data.queueLength ?? 0} queued · ${data.pendingTasks ?? 0} pending`
      case 'container.stopping':
        return 'Shutting down...'
      case 'container.stopped':
        return 'Stopped'
      case 'container.error':
        return data.error ? String(data.error).slice(0, 200) : 'Container creation failed'
      case 'message.queued':
        return data.prompt ? `"${String(data.prompt).slice(0, 80)}${String(data.prompt).length > 80 ? '...' : ''}"` : `msg ${data.messageId ?? ''}`
      case 'message.started':
        return [
          data.attempt && `attempt ${data.attempt}`,
          data.prompt && `"${String(data.prompt).slice(0, 60)}${String(data.prompt).length > 60 ? '...' : ''}"`,
        ].filter(Boolean).join(' · ') || 'Processing'
      case 'message.done':
        return [
          data.cost_usd != null && `$${Number(data.cost_usd).toFixed(2)}`,
          data.duration_ms != null && `${(Number(data.duration_ms) / 1000).toFixed(1)}s`,
          data.num_turns != null && `${data.num_turns} turns`,
        ].filter(Boolean).join(' · ') || 'Done'
      case 'message.error':
        return data.error ? String(data.error).slice(0, 100) : 'Error'
      case 'task.started':
        return [
          data.skill && String(data.skill).split('/').pop(),
          data.subtasks && `${(data.subtasks as string[]).length} subtasks`,
        ].filter(Boolean).join(' · ') || `attempt ${data.attempt ?? '?'}`
      case 'task.done': {
        const parts: string[] = []
        if (data.skill) parts.push(String(data.skill).split('/').pop()!)
        if (data.cost != null) parts.push(`$${Number(data.cost).toFixed(2)}`)
        if (data.totalCost != null) parts.push(`total: $${Number(data.totalCost).toFixed(2)}`)
        if (data.failed) parts.push('FAILED')
        return parts.join(' · ') || 'Done'
      }
      case 'log':
        return data.line ? String(data.line).slice(0, 120) : ''
      default:
        return Object.keys(data).length > 0 ? JSON.stringify(data).slice(0, 120) : ''
    }
  }

  return (
    <div data-testid="webhook-events-feed" className="webhook-events-feed">
      <button
        data-testid="webhook-events-toggle"
        className="webhook-events-toggle"
        onClick={() => setCollapsed(!collapsed)}
      >
        <span className="webhook-events-toggle-icon">{collapsed ? '\u25B6' : '\u25BC'}</span>
        <span>Recent Webhook Events ({collapsed ? 'show' : 'hide'})</span>
      </button>

      {!collapsed && (
        <div className="webhook-events-list">
          {loading && (
            <div className="webhook-events-loading">Loading events...</div>
          )}

          {error && (
            <div className="webhook-events-error">{error}</div>
          )}

          {!loading && !error && events.length === 0 && (
            <div className="webhook-events-empty">No webhook events received yet.</div>
          )}

          {!loading && events.length > 0 && (
            <table className="webhook-events-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Type</th>
                  <th>Container</th>
                  <th>Run</th>
                  <th>Data</th>
                </tr>
              </thead>
              <tbody>
                {events.map((evt) => {
                  const data = parseData(evt.data)
                  return (
                    <tr key={evt.id} data-testid={`webhook-event-${evt.id}`}>
                      <td className="event-time">{formatTimestamp(evt.timestamp)}</td>
                      <td>
                        <span className={`event-type-badge ${getEventTypeClass(evt.type)}`}>
                          {evt.type}
                        </span>
                      </td>
                      <td className="event-container">{evt.container_name}</td>
                      <td>
                        {evt.run_id ? (
                          <Link to={`/runs/${evt.run_id}`} className="event-run-link">View Run</Link>
                        ) : (
                          <span className="event-no-run">-</span>
                        )}
                      </td>
                      <td className="event-data">
                        {(() => {
                          const formatted = formatEventData(evt.type, data)
                          return formatted ? (
                            <span className={`event-data-text ${evt.type === 'log' ? 'event-data-log' : ''}`}>
                              {formatted}
                            </span>
                          ) : <span className="event-no-run">-</span>
                        })()}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  )
}

export default WebhookEventsFeed
